import { CartAPI } from '@business/api/cart';
import { StoreAPI } from '@business/api/store';
import { StoreId, StoreItemResponse, StorelineItemResponse, StoreResponse, StoreSectionResponse } from '@business/entities/store';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap, tap } from 'rxjs/operators';
import { StoreUrlRequest } from './../../entities/store/StoreResponse';

export class StoreBloc extends BaseBloc {
  private readonly storeRepo = new Repository<StoreResponse>({
    getItemId: store => store.id,
  });

  readonly stores$ = this.storeRepo.items$;
  readonly selectStore = this.storeRepo.selectItem;

  onReset() {
    this.storeRepo.reset();
  }

  fetchStores = () => {
    return StoreAPI.fetchStores().pipe(this.storeRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_store_error') }));
  };

  fetchStore = (id: StoreId) => {
    return StoreAPI.fetchStore(id).pipe(
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_store_error') }),
    );
  };

  fetchCartStore = (id: StoreId) => {
    return CartAPI.fetchStore(id).pipe(
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_store_error') }),
    );
  };

  createStore = (store: Partial<StoreResponse>) => {
    return StoreAPI.createStore(store).pipe(
      this.storeRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_store'),
      }),
    );
  };

  updateStore = (id: StoreId, store: Partial<StoreResponse>) => {
    return StoreAPI.updateStore(id, store).pipe(
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_store'),
      }),
    );
  };

  updateStoreUrls = (id: StoreId, data: StoreUrlRequest[]) => {
    return StoreAPI.updateStoreUrls(id, data).pipe(
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_store'),
      }),
    );
  };

  deleteStoreUrls = (id: StoreId, data: StoreUrlRequest[]) => {
    return showDeleteConfirmation('Checkout Url', 'Do you really want to remove this url?').pipe(
      switchMap(() => StoreAPI.updateStoreUrls(id, data)),
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_store'),
      }),
    );
  };

  deleteStore = (id: StoreId) => {
    return showDeleteConfirmation('Delete Store', 'Do you really want to remove this store?').pipe(
      switchMap(() => StoreAPI.deleteStore(id)),
      this.storeRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_store'),
      }),
    );
  };

  /* Sections */
  createSection = (storeId: StoreId, data: Partial<StoreSectionResponse>) => {
    return StoreAPI.createSection(storeId, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section'),
      }),
    );
  };

  updateSection = (storeId: StoreId, id: number, data: Partial<StoreSectionResponse>) => {
    return StoreAPI.updateSection(id, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section'),
      }),
    );
  };

  deleteSection = (storeId: StoreId, id: number) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() => StoreAPI.deleteSection(id)),
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section'),
      }),
    );
  };

  sortSections = (storeId: StoreId, sectionIds: number[]) => {
    return StoreAPI.sortSections(sectionIds).pipe(
      tap(() => {
        const checklist = this.storeRepo.getItem(storeId)! as StoreResponse;
        const newSections = checklist.sections.map(sec => {
          return { ...sec, menuOrder: R.findIndex(sectionIds, i => i === sec.id) };
        });

        this.storeRepo.updateOne({
          id: storeId,
          changes: () => ({ sections: R.sortBy(newSections, s => s.menuOrder) }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_section_sort'),
      }),
    );
  };

  /* Steps */

  createItem = (storeId: StoreId, sectionId: string, data: Partial<StoreItemResponse>) => {
    return StoreAPI.createItem(sectionId, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('create_checklist_gc_section_step'),
      }),
    );
  };

  updateItem = (storeId: StoreId, id: number, data: Partial<StoreItemResponse>) => {
    return StoreAPI.updateItem(id, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('update_checklist_gc_section_step'),
      }),
    );
  };

  deleteItem = (storeId: StoreId, id: number) => {
    return showDeleteConfirmation('Delete Item', 'Do you really want to remove this Item?').pipe(
      switchMap(() => StoreAPI.deleteItem(id)),
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('delete_checklist_gc_section_step'),
      }),
    );
  };

  sortItems = (storeId: StoreId, sectionId: number, itemIds: number[]) => {
    return StoreAPI.sortItems(itemIds).pipe(
      tap(() => {
        const store = this.storeRepo.getItem(storeId)! as StoreResponse;
        const affectedSection = R.find(store.sections, s => s.id === sectionId)!;
        const newSteps = affectedSection.items.map(step => {
          return { ...step, menuOrder: R.findIndex(itemIds, i => i === step.id) };
        });

        const newSections = store.sections.map(sec => {
          if (sec.id === sectionId) {
            return { ...sec, items: R.sortBy(newSteps, s => s.menuOrder) };
          }
          return sec;
        });

        this.storeRepo.updateOne({
          id: storeId,
          changes: () => ({ sections: newSections }),
        });
      }),
      handleMessage({
        error: requestMessage('update_checklist_steps_sort'),
      }),
    );
  };

  /* Line items */

  creteLineItem = (storeId: StoreId, itemId: number, data: Partial<StorelineItemResponse>[]) => {
    return StoreAPI.creteLineItem(itemId, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('update_store_line_item'),
      }),
    );
  };

  updateLineItem = (storeId: StoreId, id: number, data: Partial<StorelineItemResponse>[]) => {
    return StoreAPI.updateLineItem(id, data).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('update_store_line_item'),
      }),
    );
  };

  deleteLineItem = (storeId: StoreId, id: number) => {
    return showDeleteConfirmation('Delete Line Item', 'Do you really want to remove this line item?').pipe(
      switchMap(() => StoreAPI.deleteLineItem(id)),
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('delete_store_line_item'),
      }),
    );
  };

  sortLineItem = (storeId: StoreId, itemId: number, ids: number[]) => {
    return StoreAPI.sortLineItem(itemId, ids).pipe(
      switchMap(() => this.fetchStore(storeId)),
      handleMessage({
        type: requestMessage('update_store_line_item'),
      }),
    );
  };

  duplicateStore = (id: StoreId, name: string) => {
    return StoreAPI.duplicateStore(id, name).pipe(
      this.storeRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('store_duplicate'),
      }),
    );
  };
}
